import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sprout, Trees, Trophy, Target, PlusCircle, HeartHandshake, TreePine } from "lucide-react";
import { toast } from "sonner";
import GeoSearchAutocomplete from "@/components/GeoSearchAutocomplete";

/**
 * "Un progetto grande quanto il futuro che vogliamo costruire."
 * Public list of reforestation projects with progress + separate donation flow (target_type=reforestation).
 * Admin can create/edit projects and add updates inline.
 */
export default function ReforestationPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [creatOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reforestation/projects");
      setProjects(data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const donate = async (proj, amount) => {
    const amt = parseFloat(amount);
    if (!amt || amt < 0.5) { toast.error("Importo minimo: 0,50 €"); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: amt, currency: "eur",
        target_type: "reforestation", target_id: proj.project_id,
        message: `Riforestazione: ${proj.title}`,
        origin_url: window.location.origin,
      });
      const url = data.checkout_url || data.url;
      if (url) window.location.href = url;
      else toast.error("Errore checkout");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x.msg || "").join(", ") : "Errore checkout");
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="reforestation-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" />
        <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-8 md:p-12 relative overflow-hidden" data-testid="reforestation-hero">
          <div className="absolute top-0 right-0 opacity-10"><TreePine size={220} /></div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-200 mb-3"><Sprout size={12} className="inline mr-1" /> Progetto TO</div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold leading-tight max-w-3xl">{t("reforestation_big_project")}</h1>
          <p className="mt-4 max-w-2xl text-emerald-50 text-sm md:text-base">Ripiantare, rigenerare, prendersi cura. Ogni progetto qui è pensato per un territorio reale — degradato, colpito da incendi o in perdita di vegetazione — con specie adatte all'ecosistema locale e con monitoraggio nel tempo.</p>
        </div>

        {user?.role === "admin" && (
          <div className="mt-6 flex justify-end">
            <Button data-testid="admin-new-project" onClick={() => setCreateOpen(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><PlusCircle size={14} className="mr-1.5" /> Nuovo progetto</Button>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="projects-list">
          {projects.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="projects-empty">
              <TreePine className="mx-auto mb-3 text-emerald-600" size={32} />
              <div className="font-semibold text-slate-900">Nessun progetto pubblicato</div>
              <div className="text-xs mt-1">I progetti verranno pubblicati dagli Admin quando esistono informazioni reali e verificate.</div>
            </div>
          )}
          {projects.map((p) => (
            <ProjectCard key={p.project_id} p={p} user={user} onDonate={donate} onChanged={load} />
          ))}
        </div>
      </div>

      {creatOpen && <CreateProjectDialog open={creatOpen} onOpenChange={setCreateOpen} onCreated={load} />}
    </div>
  );
}

function ProjectCard({ p, user, onDonate, onChanged }) {
  const [amount, setAmount] = useState(10);
  const [editOpen, setEditOpen] = useState(false);
  const pct = p.goal_amount > 0 ? Math.min(100, Math.round((p.raised_amount || 0) / p.goal_amount * 100)) : 0;
  const treesPct = p.trees_goal > 0 ? Math.min(100, Math.round((p.trees_planted || 0) / p.trees_goal * 100)) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`project-${p.project_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><Badge className="bg-emerald-100 text-emerald-800 border-0 uppercase text-[10px]">{p.status}</Badge>
            {p.location_name && <span className="text-xs text-slate-500">{p.location_name}</span>}
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mt-1 break-words" data-testid={`project-title-${p.project_id}`}>{p.title}</h2>
        </div>
        {user?.role === "admin" && (
          <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`project-edit-${p.project_id}`} onClick={() => setEditOpen(true)}>Modifica</Button>
        )}
      </div>
      <p className="text-sm text-slate-700 mt-2 break-words line-clamp-3">{p.description}</p>
      <div className="mt-4 space-y-2">
        {p.goal_amount > 0 && (
          <div>
            <div className="flex justify-between text-[11px] text-slate-500 mb-1"><span><Target size={10} className="inline mr-1" /> Obiettivo € {p.goal_amount.toLocaleString()}</span><span>€ {(p.raised_amount||0).toLocaleString()} · {pct}%</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} data-testid={`project-goal-bar-${p.project_id}`} /></div>
          </div>
        )}
        {p.trees_goal > 0 && (
          <div>
            <div className="flex justify-between text-[11px] text-slate-500 mb-1"><span><Trees size={10} className="inline mr-1" /> Alberi (piantati/finanziati/obiettivo)</span><span data-testid={`project-trees-${p.project_id}`}>{p.trees_planted||0} / {p.trees_financed||0} / {p.trees_goal}</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-lime-500" style={{ width: `${treesPct}%` }} /></div>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Input type="number" min="0.50" step="0.10" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-24 h-9 rounded-full text-sm" data-testid={`project-donate-amount-${p.project_id}`} />
        <Button data-testid={`project-donate-${p.project_id}`} onClick={() => onDonate(p, amount)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9"><HeartHandshake size={14} className="mr-1.5" /> Dona per la riforestazione</Button>
        <span className="text-[10px] text-slate-500">Importo minimo: 0,50 €</span>
      </div>
      {p.updates?.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-emerald-700 cursor-pointer font-semibold">Aggiornamenti ({p.updates.length})</summary>
          <div className="mt-2 space-y-2">
            {p.updates.slice(-5).reverse().map((u) => (
              <div key={u.update_id} className="text-xs text-slate-700 bg-slate-50 rounded-lg p-2" data-testid={`project-update-${u.update_id}`}>
                <div className="text-[10px] text-slate-400">{new Date(u.at).toLocaleString()} · {u.by_name}</div>
                <div className="break-words">{u.text}</div>
              </div>
            ))}
          </div>
        </details>
      )}
      {editOpen && <EditProjectDialog open={editOpen} onOpenChange={setEditOpen} project={p} onSaved={onChanged} />}
    </div>
  );
}

function CreateProjectDialog({ open, onOpenChange, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", description: "", goal_amount: 5000, trees_goal: 250, location_name: "" });
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title || !form.description) { toast.error("Titolo e descrizione richiesti"); return; }
    setBusy(true);
    try {
      await api.post("/reforestation/projects", {
        ...form, goal_amount: parseFloat(form.goal_amount) || 0, trees_goal: parseInt(form.trees_goal) || 0,
        location_gid: loc?.id, location_name: loc?.display || form.location_name,
        lat: loc?.lat, lng: loc?.lng,
      });
      toast.success("Progetto creato");
      onCreated && onCreated(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-project-dialog">
        <DialogHeader><DialogTitle>{t("admin_reforestation_new")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cp-title" className="rounded-xl" />
          <Textarea placeholder="Descrizione (obiettivo, aree, specie, monitoraggio…)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cp-desc" rows={4} className="rounded-xl" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Località</div>
            <GeoSearchAutocomplete value={loc} onChange={setLoc} testid="cp-geo" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Obiettivo €</div>
              <Input type="number" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} data-testid="cp-goal" className="rounded-xl" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Alberi obiettivo</div>
              <Input type="number" value={form.trees_goal} onChange={(e) => setForm({ ...form, trees_goal: e.target.value })} data-testid="cp-trees" className="rounded-xl" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={busy} data-testid="cp-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProjectDialog({ open, onOpenChange, project, onSaved }) {
  const { t } = useI18n();
  const [tf, setTf] = useState(project.trees_financed || 0);
  const [tp, setTp] = useState(project.trees_planted || 0);
  const [status, setStatus] = useState(project.status || "active");
  const [update, setUpdate] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    try {
      await api.patch(`/reforestation/projects/${project.project_id}`, { trees_financed: parseInt(tf) || 0, trees_planted: parseInt(tp) || 0, status });
      if (update.trim()) {
        await api.post(`/reforestation/projects/${project.project_id}/updates`, { text: update.trim() });
      }
      toast.success("Salvato"); onSaved && onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/reforestation/projects/${project.project_id}`);
      toast.success(t("deleted") || "Eliminato");
      setConfirmingDelete(false);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-project-dialog">
        <DialogHeader><DialogTitle>{t("admin_reforestation_update")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Alberi finanziati</div>
            <Input type="number" value={tf} onChange={(e) => setTf(e.target.value)} data-testid="ep-trees-financed" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Alberi piantati</div>
            <Input type="number" value={tp} onChange={(e) => setTp(e.target.value)} data-testid="ep-trees-planted" />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Nuovo aggiornamento (facoltativo)</div>
          <Textarea rows={3} value={update} onChange={(e) => setUpdate(e.target.value)} data-testid="ep-update-text" />
        </div>
        {confirmingDelete ? (
          <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="ep-delete-confirm">
            <div className="font-semibold mb-2">Eliminare definitivamente questo progetto?</div>
            <div className="text-xs mb-3">L'operazione non è reversibile. Le donazioni già ricevute restano registrate nella cronologia pagamenti.</div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmingDelete(false)} disabled={busy} data-testid="ep-delete-cancel">Annulla</Button>
              <Button onClick={remove} disabled={busy} data-testid="ep-delete-yes" className="bg-red-600 hover:bg-red-700 text-white hover:text-white">Elimina</Button>
            </div>
          </div>
        ) : null}
        <DialogFooter className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setConfirmingDelete(true)}
            data-testid="ep-delete-open"
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
          >{t("delete") || "Elimina"}</Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="ep-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("save")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
